import type {
  HaloPostRaw,
  HaloPostsResponse,
  PostItem,
  PostStatus,
  HaloTagsResponse,
  TagItem,
  HaloCategoriesResponse,
  HaloCategoryRaw,
  CategoryItem
} from '@/types'

function getPostStatus(post: HaloPostRaw): PostStatus {
  if (post.spec.deleted) return 'deleted'
  if (post.spec.publish) return 'published'
  return 'draft'
}

export function buildCategoryNameMap(data: HaloCategoriesResponse | null): Map<string, string> {
  const map = new Map<string, string>()
  if (!data || !data.items) return map

  const categoryMap = new Map<string, HaloCategoryRaw>()
  for (const cat of data.items) {
    categoryMap.set(cat.metadata.name, cat)
  }

  function findParent(name: string): HaloCategoryRaw | null {
    for (const [, cat] of categoryMap) {
      if (cat.spec.children?.includes(name)) {
        return cat
      }
    }
    return null
  }

  function buildHierarchicalName(name: string): string {
    const cat = categoryMap.get(name)
    if (!cat) return name
    const parent = findParent(name)
    if (parent) {
      return `${parent.spec.displayName} > ${cat.spec.displayName}`
    }
    return cat.spec.displayName
  }

  for (const cat of data.items) {
    map.set(cat.metadata.name, buildHierarchicalName(cat.metadata.name))
  }

  return map
}

export function processPostsData(
  data: HaloPostsResponse | null,
  categoryNameMap?: Map<string, string>
): PostItem[] {
  if (!data || !data.items) return []

  return data.items.map((post: HaloPostRaw): PostItem => ({
    id: post.metadata.name,
    title: post.spec.title,
    slug: post.spec.slug || '',
    permalink: post.status?.permalink || '',
    author: post.owner?.displayName || '未知作者',
    categories: post.categories?.map(cat => {
      return categoryNameMap?.get(cat.metadata.name) ?? cat.spec.displayName
    }) || [],
    publishTime: post.spec.publishTime || post.metadata.creationTimestamp,
    status: getPostStatus(post),
    comments: post.stats?.comment || 0,
    visits: post.stats?.visit || 0,
    upvotes: post.stats?.upvote || 0,
    excerpt: post.spec.excerpt?.raw || '',
    tags: post.tags?.map(tag => tag.spec.displayName) || []
  }))
}

export function processTagsData(data: HaloTagsResponse | null): TagItem[] {
  if (!data || !data.items) return []

  return data.items.map((tag): TagItem => ({
    name: tag.metadata.name,
    displayName: tag.spec.displayName,
    slug: tag.spec.slug,
    postCount: tag.status?.visiblePostCount ?? tag.status?.postCount ?? tag.postCount ?? 0,
    permalink: tag.status?.permalink || ''
  })).sort((a, b) => b.postCount - a.postCount)
}

export function processCategoriesData(data: HaloCategoriesResponse | null): CategoryItem[] {
  if (!data || !data.items) return []

  const categoryMap = new Map<string, HaloCategoryRaw>()
  for (const cat of data.items) {
    categoryMap.set(cat.metadata.name, cat)
  }

  function findParentName(name: string): string {
    for (const [, cat] of categoryMap) {
      if (cat.spec.children?.includes(name)) {
        return cat.metadata.name
      }
    }
    return ''
  }

  return data.items.map(cat => ({
    name: cat.metadata.name,
    displayName: cat.spec.displayName,
    slug: cat.spec.slug,
    parentName: findParentName(cat.metadata.name),
    children: cat.spec.children || [],
    postCount: cat.status?.visiblePostCount ?? cat.status?.postCount ?? 0,
    permalink: cat.status?.permalink || ''
  }))
}
